import dataFormatter from '@/services/format/dataFormatter.js'
import { ALERT_TYPES, sortAlerts, isAlertEnabled } from '@/services/alerts/alertsCatalog'
import capacity from '@/services/alerts/capacity'
import { postsPhrase, itemsPhrase, enumerate, shortageGroupsPhrase } from '@/services/alerts/wording'

/**
 * Détection des alertes d'une équipe.
 *
 * Rien n'est stocké : chaque alerte est déduite des données déjà chargées par
 * l'application. Le service est volontairement pur — il reçoit un contexte, il renvoie
 * une liste — pour être appelé aussi bien par le centre de notifications que par les
 * écrans qui demandent confirmation avant une action risquée.
 *
 * Les contrôles portant sur le mois en cours sont des indications, pas des verdicts : la
 * réalisation des contrats est décidée par le moteur au calcul du mois, avec des règles
 * de répartition horaire que le client ne rejoue pas. Les libellés le disent.
 */

const CONTRACT_FAILURE_LABELS = {
  'NO STAFF': 'aucun collaborateur en état de travailler ne réunissait les qualités requises',
  'NO TOOLS': 'outillage insuffisant',
  'NO SUPPLIER': 'aucun fournisseur',
  'NO SUPPLIER STAFF': 'aucun collaborateur en état d\'assurer le métier exigé par le fournisseur',
  OLD: 'contrat arrivé à échéance',
}

/** Nom lisible d'un élément de référentiel, l'identifiant en dernier recours. */
function assetName(list, id) {
  const found = (list || []).find(item => item.id === id || item.value === id)
  return found ? (found.name || found.adminName || String(id)) : String(id)
}

function names(list, ids) {
  return Array.from(ids).map(id => assetName(list, id)).join(', ')
}

/** Résolveur de nom prêt pour les fonctions de mise en français. */
function nameResolver(list) {
  return id => assetName(list, id)
}

/** Base horaire d'un temps plein, celle du paramètre d'encadrement du moteur. */
const FULL_TIME_HOURS = 35

/** Les heures sont des flottants : un écart infime n'est pas un manque. */
const HOURS_TOLERANCE = 1e-6

/**
 * Écart toléré entre les heures de contrat et les heures de production.
 *
 * En deçà, l'équipe est considérée à l'équilibre : un carnet ne tombe jamais pile sur la
 * capacité, et signaler trois heures d'écart n'aiderait personne. Au delà, le déséquilibre
 * coûte quelque chose dans un sens comme dans l'autre — des heures supplémentaires et des
 * contrats mal honorés d'un côté, des salaires payés pour rien de l'autre.
 */
const WORKLOAD_TOLERANCE_RATIO = 0.1

const alertsService = {
  CONTRACT_FAILURE_LABELS,

  /**
   * @param {object} context données de l'équipe et référentiels
   * @param {object} context.team équipe concernée
   * @param {object} context.strategy domaines de stratégie de la période en cours
   * @param {object} context.previousState domaines de l'état du mois précédent
   * @param {object} context.assets référentiels : jobs, offers, buildingTypes, stuffTypes,
   *   toolTypes, buildings, stuffs, tools
   * @param {object[]} context.depotAreas zones de dépôt visibles
   * @param {object[]} context.depots dépôts déjà remis par cette équipe
   * @param {object[]} context.members membres de l'équipe, pour les zones personnelles
   * @param {object} context.parameters paramètres généraux de l'application
   * @param {object} context.alertSettings types désactivés par l'administrateur
   * @returns {object[]} alertes triées, de la plus grave à la plus anodine
   */
  detect(context) {
    if (!context || !context.team) return []

    const alerts = []
    const push = (type, key, detail, extra = {}) => {
      const definition = ALERT_TYPES[type]
      if (!definition) return
      // Un type désactivé dans les paramètres ne remonte pour personne, joueur comme
      // administrateur. Le filtre est posé ici, au plus près de la création : aucune
      // détection ne peut le contourner par mégarde.
      if (!isAlertEnabled(context.alertSettings, type)) return
      alerts.push({
        id: `${context.team.id}-${type}-${key}`,
        type,
        severity: definition.severity,
        title: definition.title,
        route: definition.route,
        detail,
        teamId: context.team.id,
        teamName: context.team.name,
        ...extra,
      })
    }

    const reportedTools = this.detectCapacity(context, push) || []
    this.detectSetup(context, push)
    this.detectContracts(context, push, reportedTools)
    this.detectIdleStaff(context, push)
    this.detectStatuses(context, push)
    this.detectFinances(context, push)
    this.detectDepots(context, push)
    this.detectIdentity(context, push)

    return sortAlerts(alerts)
  },

  /** Locaux, équipements et outils manquants pour l'effectif retenu. */
  detectCapacity(context, push) {
    const { strategy, assets } = context
    if (!strategy || !strategy.staff) return

    const staffList = Object.values(strategy.staff.update || {})
    if (!staffList.length) return []

    const space = this.buildSpace(context)
    const { missing, shortages } = capacity.consume({ staffList, jobsTable: assets.jobsTable, space })

    // Équipements et outils sont rapportés par groupes de collaborateurs concernés, et non
    // par totaux : un même élément sert à plusieurs métiers, et c'est le métier qui dit
    // quoi acheter.
    const groups = dimension => shortageGroupsPhrase(
      shortages, dimension,
      nameResolver(assets.jobs),
      nameResolver(dimension === 'tools' ? assets.toolTypes : assets.stuffTypes))

    if (missing.buildings.size) {
      push('missingBuilding', 'staff',
        `Il vous manque ${postsPhrase(missing.buildings, nameResolver(assets.buildingTypes))} dans vos locaux actuels.`)
    }
    if (missing.stuffs.size) {
      push('missingStuff', 'staff', `${groups('stuffs')}.`)
    }
    if (missing.tools.size) {
      push('missingTool', 'staff', `${groups('tools')}.`)
    }

    // Rendus à l'appelant pour ne pas signaler deux fois le même outil.
    return Array.from(missing.tools.keys())
  },

  /** Offre, fournisseur et encadrement : ce sans quoi le mois n'a rien à produire. */
  detectSetup(context, push) {
    const { strategy, assets } = context
    if (!strategy) return

    if (strategy.offers && !Object.keys(strategy.offers.update || {}).length) {
      push('noOffer', 'none', 'Rien à vendre ce mois-ci.')
    }

    if (strategy.supplier && !strategy.supplier.supplier) {
      push('noSupplier', 'none', 'Les contrats qui en dépendent échoueront.')
    }

    this.detectManagement(context, push)
  },

  /**
   * Déficit d'encadrement, mesuré comme le fait le moteur.
   *
   * Le paramètre général `managerRatio` exprime un ratio optimal sous la forme
   * « 35 h d'encadrement pour N salariés » : chaque collaborateur de production réclame
   * donc `35 / N` heures d'encadrement, et le déficit est converti en encadrants à temps
   * plein, plus parlant qu'un volume horaire.
   */
  detectManagement(context, push) {
    const { strategy, assets, parameters } = context
    if (!strategy || !strategy.staff) return

    const ratio = parseFloat(parameters && parameters.managerRatio)
    if (!ratio || ratio <= 0) return

    const staffList = Object.values(strategy.staff.update || {})

    let managedCount = 0
    let managerHours = 0
    for (const staff of staffList) {
      const job = assets.jobsTable[staff.job]
      if (!job) continue
      if (job.type === 'production') managedCount += 1
      else if (job.type === 'management') managerHours += parseFloat(staff.hours) || 0
    }

    if (!managedCount) return

    const requiredHours = managedCount * (FULL_TIME_HOURS / ratio)
    const shortfall = requiredHours - managerHours
    if (shortfall <= HOURS_TOLERANCE) return

    const missing = Math.ceil(shortfall / FULL_TIME_HOURS)
    const managers = `${missing} encadrant${missing > 1 ? 's' : ''} manquant${missing > 1 ? 's' : ''}`
    const managed = `${managedCount} collaborateur${managedCount > 1 ? 's' : ''}`

    push('noManager', 'none', managerHours > 0
      ? `${managers} pour ${managed}.`
      : `Aucun encadrant : il en faudrait ${missing} pour ${managed}.`)
  },

  /**
   * Contrats que le moteur n'a pas pu réaliser au dernier mois calculé.
   *
   * Le verdict est certain, contrairement aux contrôles portant sur le mois en cours. Une
   * seule alerte les rassemble : une par contrat noierait le reste du bandeau.
   *
   * Le constat est rangé dans `contractInfos` sur l'élément d'état, pas à sa racine.
   */
  detectFailedContracts(context, push) {
    const { previousState, assets } = context
    const items = (previousState && previousState.contracts && previousState.contracts.items) || {}

    const failed = []
    for (const contract of Object.values(items)) {
      const infos = contract.contractInfos || contract
      if (!infos.failed) continue
      failed.push({
        offer: assetName(assets.offers, contract.offer),
        reason: CONTRACT_FAILURE_LABELS[infos.message] || infos.message || 'raison inconnue',
      })
    }
    if (!failed.length) return

    // Un seul motif pour tous : le nommer vaut mieux que lister les contrats.
    const reasons = new Set(failed.map(item => item.reason))
    // Deux contrats de la même offre ne doivent pas la nommer deux fois.
    const offerNames = Array.from(new Set(failed.map(item => item.offer)))
    const count = `${failed.length} contrat${failed.length > 1 ? 's' : ''}`

    const detail = reasons.size === 1
      ? `${count} non réalisé${failed.length > 1 ? 's' : ''} : ${Array.from(reasons)[0]}.`
      : `${count} non réalisé${failed.length > 1 ? 's' : ''} : ${enumerate(offerNames.slice(0, 3))}${offerNames.length > 3 ? '...' : ''}.`

    push('contractFailed', 'previous', detail)
  },

  /**
   * Collaborateurs privés de leurs conditions de travail au dernier mois calculé.
   *
   * Le moteur inscrit ce constat sur chaque fiche : ils ont été payés sans rien produire.
   */
  detectIdleStaff(context, push) {
    const { previousState } = context
    const items = (previousState && previousState.staff && previousState.staff.items) || {}

    const idle = Object.values(items).filter(staff => staff.workplace && staff.workplace.ok === false)
    if (!idle.length) return

    push('staffIdle', 'previous',
      `${idle.length} collaborateur${idle.length > 1 ? 's' : ''} n'${idle.length > 1 ? 'ont' : 'a'} pas pu travailler faute de local, de poste équipé ou d'outillage.`)
  },

  /**
   * Contrats signés : ce qui a échoué au dernier mois calculé, et ce qui semble hors de
   * portée pour le mois en cours.
   */
  detectContracts(context, push, reportedTools = []) {
    const { strategy, previousState, assets } = context

    this.detectFailedContracts(context, push)

    if (!strategy || !strategy.contracts || !strategy.staff) return

    const contracts = Object.values(strategy.contracts.update || {})
    if (!contracts.length) return

    const staffList = Object.values(strategy.staff.update || {})
    const productionStaff = staffList.filter(staff => {
      const job = assets.jobsTable[staff.job]
      return job && job.type === 'production'
    })

    const tools = capacity.toolStock({
      strategyTools: strategy.tools,
      previousStateTools: previousState && previousState.tools,
      toolsTable: assets.toolsTable,
    })

    const missingJobs = new Set()
    const missingSkills = new Set()
    const missingTools = new Set()
    let requiredHours = 0

    for (const contract of contracts) {
      const offer = (assets.offers || []).find(item => item.id === contract.offer)
      if (!offer) continue
      const amount = parseFloat(contract.amount) || 0

      for (const optionId of Object.keys(contract.config || {})) {
        const option = (offer.options || []).find(item => item.uuid === optionId)
        if (!option) continue

        for (const choiceId of contract.config[optionId] || []) {
          const choice = (option.choices || []).find(item => item.uuid === choiceId)
          if (!choice) continue

          requiredHours += (parseFloat(choice.productionTime) || 0) * amount

          let requiredJob = null
          const requiredSkills = {}
          for (const requirement of choice.requirements || []) {
            if (requirement.type === 'jobs') requiredJob = requirement.object
            else if (requirement.type === 'skills') requiredSkills[requirement.object] = requirement.level
            else if (requirement.type === 'toolTypes') {
              if (!capacity.hasLevel(tools[requirement.object], requirement.level)) {
                missingTools.add(requirement.object)
              }
            }
          }

          const candidates = productionStaff.filter(staff => !requiredJob || staff.job === requiredJob)
          if (!candidates.length) {
            if (requiredJob) missingJobs.add(requiredJob)
            else missingJobs.add('__any__')
            continue
          }

          for (const skillId of Object.keys(requiredSkills)) {
            const covered = candidates.some(staff =>
              (staff.skills || {})[skillId] >= requiredSkills[skillId])
            if (!covered) missingSkills.add(skillId)
          }
        }
      }
    }

    if (missingJobs.size) {
      const listed = Array.from(missingJobs).filter(id => id !== '__any__')
      const detail = listed.length
        ? `Aucun collaborateur pour le métier : ${names(assets.jobs, listed)}.`
        : 'Aucun collaborateur de production pour vos contrats.'
      push('contractAtRisk', 'jobs', detail)
    }
    if (missingSkills.size) {
      push('contractAtRisk', 'skills',
        `Compétences non couvertes : ${names(assets.skills, missingSkills)}.`)
    }
    const unreportedTools = Array.from(missingTools).filter(id => !reportedTools.includes(id))
    if (unreportedTools.length) {
      push('missingTool', 'contracts',
        `Vos contrats réclament : ${names(assets.toolTypes, unreportedTools)}.`)
    }

    const availableHours = productionStaff
      .reduce((total, staff) => total + (parseFloat(staff.productionHours) || 0), 0)

    this.detectWorkloadBalance(context, push, requiredHours, availableHours)
  },

  /**
   * Équilibre entre le carnet de contrats et la capacité de production.
   *
   * Les deux volumes sont ceux que compare le moteur au calcul du mois : les heures
   * réclamées par les options des contrats signés d'un côté, les heures mensuelles des
   * collaborateurs de production de l'autre.
   *
   * Deux situations, et une seule alerte à la fois :
   *  - au delà du seuil côté contrats : l'équipe ne tiendra ses engagements qu'en heures
   *    supplémentaires, et pas forcément jusqu'au bout ;
   *  - au delà du seuil côté effectif : les salaires sont payés sans contrepartie ;
   *  - en deçà du seuil, rien n'est signalé : quelques heures se rattrapent, et le
   *    déséquilibre horaire est déjà le sujet des deux alertes ci-dessus.
   *
   * Le contrôle n'a lieu que si l'équipe a des contrats et des heures de production : sans
   * l'un ou sans l'autre, le rapport n'a pas de sens, et le début de mois ferait remonter
   * l'alerte à toutes les équipes avant qu'elles aient eu le temps de décider quoi que ce
   * soit. Ces deux cas ont déjà leurs propres alertes.
   *
   * @param {number} requiredHours heures réclamées par les contrats signés
   * @param {number} availableHours heures de production de l'effectif retenu
   */
  detectWorkloadBalance(context, push, requiredHours, availableHours) {
    if (availableHours <= HOURS_TOLERANCE || requiredHours <= HOURS_TOLERANCE) return

    const gap = requiredHours - availableHours
    const ratio = gap / availableHours
    // Un écart de 10,4 % annoncé « 10 % » ferait douter du seuil : on garde une décimale
    // tant que l'écart est faible.
    const raw = Math.abs(ratio) * 100
    const percent = dataFormatter.formatNumber(raw < 20 ? Math.round(raw * 10) / 10 : Math.round(raw))
    const hours = value => `${dataFormatter.formatNumber(Math.round(value))} h`

    if (ratio > WORKLOAD_TOLERANCE_RATIO) {
      push('overbookedHours', 'balance',
        `Vos contrats réclament ${hours(requiredHours)} pour ${hours(availableHours)} de production disponible, `
        + `soit ${percent} % de trop. Embauchez, ou allégez votre carnet : le dépassement se paiera `
        + `en heures supplémentaires, et tout ne sera pas forcément honoré.`)
      return
    }

    if (ratio < -WORKLOAD_TOLERANCE_RATIO) {
      push('idleCapacity', 'balance',
        `Votre effectif offre ${hours(availableHours)} de production pour ${hours(requiredHours)} de contrats, `
        + `soit ${percent} % de capacité inemployée. Signez davantage, ou réduisez la voile : `
        + `ces heures sont payées sans rien produire.`)
    }
  },

  /**
   * Statuts particuliers portés par les collaborateurs et par les contrats.
   *
   * Une alerte par statut rencontré, avec son effectif : « Démission — 2 collaborateurs
   * concernés ». Les regrouper en une seule ligne ferait perdre l'information utile,
   * puisque c'est le statut qui dit quoi faire, pas le nombre.
   *
   * Seuls les statuts du catalogue sont signalés. Les trois états intégrés au code —
   * « Normal », et les deux marqueurs de l'embauche et du licenciement, de la signature
   * et de la résiliation — traduisent une décision que l'équipe vient de prendre : la lui
   * annoncer n'apprendrait rien. Ils sont écartés sans liste d'exclusion, simplement
   * parce que le référentiel chargé ne contient que les statuts créés dans la session.
   */
  detectStatuses(context, push) {
    const { strategy, assets } = context
    if (!strategy || !assets) return

    this.pushStatusAlerts({
      push,
      type: 'staffStatus',
      items: strategy.staff && strategy.staff.update,
      statusTable: assets.hrStatusTable,
      noun: 'collaborateur',
    })

    this.pushStatusAlerts({
      push,
      type: 'contractStatus',
      items: strategy.contracts && strategy.contracts.update,
      statusTable: assets.contractStatusTable,
      noun: 'contrat',
    })
  },

  /**
   * Effectif par statut, une alerte par statut.
   *
   * Le titre porte le nom du statut plutôt que le libellé générique du type : c'est lui
   * que le lecteur cherche, et deux statuts différents ne doivent pas se ressembler dans
   * la liste. Le type, lui, reste celui du catalogue — c'est ce qui donne la couleur, le
   * classement et l'interrupteur des paramètres.
   */
  pushStatusAlerts({ push, type, items, statusTable, noun }) {
    if (!statusTable) return

    const counts = new Map()
    for (const item of Object.values(items || {})) {
      const id = item && item.status && item.status.id
      // Un statut absent du référentiel est un état intégré au code, ou un statut
      // supprimé du catalogue : dans les deux cas il n'y a rien à signaler.
      if (!id || !statusTable[id]) continue
      counts.set(id, (counts.get(id) || 0) + 1)
    }

    for (const [id, count] of counts) {
      const status = statusTable[id]
      const name = status.name || 'Statut sans nom'
      push(type, id,
        `${count} ${noun}${count > 1 ? 's' : ''} concerné${count > 1 ? 's' : ''}.`,
        { title: name, statusId: id, statusName: name, count })
    }
  },

  /** Trésorerie, résultat et emprunts, lus sur le dernier mois calculé. */
  detectFinances(context, push) {
    const { previousState } = context
    const infos = previousState && previousState.results && previousState.results.infos
    if (infos) {
      if (parseFloat(infos.treasury) < 0) {
        push('negativeTreasury', 'none',
          `${dataFormatter.formatMoney(infos.treasury)} au dernier mois calculé.`)
      }
      if (parseFloat(infos.result) < 0) {
        push('negativeResult', 'none',
          `${dataFormatter.formatMoney(infos.result)} au dernier mois calculé.`)
      }
    }

    for (const loan of Object.values((previousState && previousState.loans && previousState.loans.items) || {})) {
      const remaining = parseFloat(loan.remaining) || 0
      if (remaining <= 0) continue

      // L'élément d'état nomme la durée `duration` ; `delay` est le nom porté par la
      // stratégie au moment de la souscription. Lire les deux évite une échéance à zéro.
      const duration = parseFloat(loan.duration) || parseFloat(loan.delay) || 0
      const payment = duration > 0
        ? (parseFloat(loan.amount) || 0) / duration + remaining * (parseFloat(loan.rate) || 0) / 100
        : 0

      push('runningLoan', loan.uuid,
        `Reste ${dataFormatter.formatMoney(remaining)}, prochaine échéance ${dataFormatter.formatMoney(payment)}.`)
    }
  },

  /**
   * Zones de dépôt ouvertes qui attendent encore quelque chose.
   *
   * Une zone ordinaire attend un dépôt de l'équipe : un seul suffit, l'alerte est
   * collective. Une zone personnelle (`isPrivate`) attend un dépôt de chacun — un
   * participant n'y voit que ses propres fichiers — l'alerte est donc nominative, et il y
   * en a autant que de membres n'ayant rien remis.
   */
  detectDepots(context, push) {
    const { team, depotAreas, depots, members } = context
    if (!Array.isArray(depotAreas) || !Array.isArray(depots)) return

    for (const area of depotAreas) {
      if (!area.isVisible || area.isAdmin) continue

      if (!area.isPrivate) {
        const delivered = depots.some(depot => depot.location === area.id && depot.team === team.id)
        if (!delivered) push('missingDepot', area.id, `Rien déposé dans "${area.name}".`)
        continue
      }

      for (const member of members || []) {
        const delivered = depots.some(depot => depot.location === area.id && depot.user === member.id)
        if (delivered) continue

        // Un participant lit sa propre alerte : la nommer serait étrange.
        push('missingDepot', `${area.id}-${member.id}`,
          member.self
            ? `Rien déposé dans "${area.name}".`
            : `${member.name} n'a rien déposé dans "${area.name}".`,
          { userId: member.id, userName: member.name })
      }
    }
  },

  /**
   * Identité de l'entreprise : nom et logo choisis par l'équipe.
   *
   * À la création, `publicName` reprend le nom donné par l'administrateur et `logo` est
   * vide. Tant que ces deux valeurs n'ont pas bougé, l'équipe ne s'est pas approprié son
   * entreprise — ce n'est pas bloquant, mais cela se voit sur tous ses documents.
   */
  detectIdentity(context, push) {
    const { team } = context
    if (!team) return

    const publicName = String(team.publicName || '').trim()
    const adminName = String(team.name || '').trim()

    if (!publicName || publicName === adminName) {
      push('defaultCompanyName', 'none', 'Aucun nom d\'entreprise défini.')
    }

    if (!team.logo) {
      push('missingCompanyLogo', 'none', 'Aucun logo n\'a été choisi.')
    }
  },

  /** Capacité d'accueil de l'équipe, achats de la période compris. */
  buildSpace(context) {
    const { strategy, previousState, assets } = context
    return {
      buildings: capacity.buildingSpace({
        strategyBuildings: strategy && strategy.buildings,
        previousStateBuildings: previousState && previousState.buildings,
        buildingsTable: assets.buildingsTable,
      }),
      stuffs: capacity.stuffSpace({
        strategyStuffs: strategy && strategy.stuffs,
        previousStateStuffs: previousState && previousState.stuffs,
        stuffsTable: assets.stuffsTable,
      }),
      tools: capacity.toolStock({
        strategyTools: strategy && strategy.tools,
        previousStateTools: previousState && previousState.tools,
        toolsTable: assets.toolsTable,
      }),
    }
  },

  /**
   * Phrase listant ce qui manquerait après le retrait d'un local ou d'un équipement,
   * à présenter avant confirmation. Renvoie `null` si le retrait ne prive personne.
   *
   * @param {object} removal `{ buildingType, posts }` ou `{ stuffType, level, posts }`
   */
  describeMissingAfterRemoval({ context, removal }) {
    const { strategy, assets } = context
    const space = this.buildSpace(context)

    if (removal.buildingType) {
      space.buildings[removal.buildingType] =
        (space.buildings[removal.buildingType] || 0) - (parseInt(removal.posts) || 0)
    }
    if (removal.stuffType && space.stuffs[removal.stuffType]) {
      const level = parseInt(removal.level) || 0
      space.stuffs[removal.stuffType][level] -= (parseInt(removal.posts) || 0)
    }

    const { missing } = capacity.consume({
      staffList: Object.values((strategy && strategy.staff && strategy.staff.update) || {}),
      jobsTable: assets.jobsTable,
      space,
    })

    // Seule la dimension retirée est rapportée. L'écran qui pose la question ne connaît
    // que son propre domaine — la page des locaux ignore l'équipement détenu — et
    // rapporter les autres dimensions ferait état de manques imaginaires, désignés par
    // des identifiants que cet écran ne sait pas traduire.
    if (removal.buildingType) {
      return missing.buildings.size
        ? `Après ce retrait, il vous manquera ${postsPhrase(missing.buildings, nameResolver(assets.buildingTypes))}.`
        : null
    }

    if (removal.stuffType) {
      return missing.stuffs.size
        ? `Après ce retrait, il vous manquera ${itemsPhrase(missing.stuffs, nameResolver(assets.stuffTypes))}.`
        : null
    }

    return null
  },

  /**
   * Phrase listant ce qui manque pour une embauche, à présenter avant confirmation.
   * Renvoie `null` lorsque rien ne manque.
   */
  describeMissingForStaff({ candidateJobId, context }) {
    const { strategy, assets } = context
    const missing = capacity.missingForStaff({
      candidateJobId,
      staffList: Object.values((strategy && strategy.staff && strategy.staff.update) || {}),
      jobsTable: assets.jobsTable,
      space: this.buildSpace(context),
    })

    const parts = []
    if (missing.buildings.size) parts.push(postsPhrase(missing.buildings, nameResolver(assets.buildingTypes)))
    if (missing.stuffs.size) parts.push(itemsPhrase(missing.stuffs, nameResolver(assets.stuffTypes)))
    if (missing.tools.size) parts.push(itemsPhrase(missing.tools, nameResolver(assets.toolTypes)))

    return parts.length ? `Il vous manque ${enumerate(parts)}.` : null
  },
}

export default alertsService
